import { subDays } from 'date-fns/subDays';
import { endOfWeek } from 'date-fns/endOfWeek';
import { startOfWeek } from 'date-fns/startOfWeek';
import { toDate, formatInTimeZone } from 'date-fns-tz';

/** ISO-style week in the store calendar: Monday–Sunday. */
const WEEK_STARTS_ON = 1;

/**
 * Noon on the Monday of the Mon–Sun week that contains store-calendar `ymd` in `timezone`.
 * (Do not use date-fns `startOfWeek` on a zoned instant — it uses the browser local calendar.)
 */
function storeMondayNoonOfWeekContainingYmd(timezone, ymd) {
  const noon = toDate(`${ymd}T12:00:00`, { timeZone: timezone });
  const isoDow = Number(formatInTimeZone(noon, timezone, 'i')); // 1 = Mon … 7 = Sun
  const daysFromMonday = isoDow - 1;
  return subDays(noon, daysFromMonday);
}

/**
 * Start of a store-calendar day (wall yyyy-MM-dd in IANA `timezone`) as a UTC Date.
 */
export function storeStartOfCalendarDay(timezone, ymd) {
  if (!timezone || !ymd) return null;
  return toDate(`${ymd}T00:00:00`, { timeZone: timezone });
}

/**
 * End of a store-calendar day (23:59:59.999 in that zone) as a UTC Date.
 */
export function storeEndOfCalendarDay(timezone, ymd) {
  if (!timezone || !ymd) return null;
  return toDate(`${ymd}T23:59:59.999`, { timeZone: timezone });
}

/**
 * API bounds: treat match.start/end as instants, take their calendar dates in the store zone,
 * then full store-local days for those dates.
 */
export function storeApiBoundsFromMatch(timezone, matchStart, matchEnd) {
  const startYmd = formatInTimeZone(matchStart, timezone, 'yyyy-MM-dd');
  const endYmd = formatInTimeZone(matchEnd, timezone, 'yyyy-MM-dd');
  return {
    start: storeStartOfCalendarDay(timezone, startYmd),
    end: storeEndOfCalendarDay(timezone, endYmd),
  };
}

/**
 * Last `n` inclusive store-calendar days ending on **yesterday** in `timezone`
 * (same semantics as legacy Product Analysis presets).
 */
export function storeMatchRangeLastNCalendarDays(timezone, n) {
  if (!timezone || n < 1) {
    const endTime = new Date();
    endTime.setHours(0, 0, 0, 0);
    endTime.setDate(endTime.getDate() - 1);
    const time = new Date(endTime);
    time.setDate(time.getDate() - (n - 1));
    return { start: time, end: endTime };
  }
  const todayYmd = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const todayAnchor = toDate(`${todayYmd}T12:00:00`, { timeZone: timezone });
  const yesterdayAnchor = subDays(todayAnchor, 1);
  const endYmd = formatInTimeZone(yesterdayAnchor, timezone, 'yyyy-MM-dd');
  const startAnchor = subDays(yesterdayAnchor, n - 1);
  const startYmd = formatInTimeZone(startAnchor, timezone, 'yyyy-MM-dd');
  return {
    start: storeStartOfCalendarDay(timezone, startYmd),
    end: storeEndOfCalendarDay(timezone, endYmd),
  };
}

/**
 * Last `n` inclusive store-calendar days ending on **today** in `timezone`
 * (for list filters that previously used “today at midnight” local end).
 */
export function storeMatchRangeLastNCalendarDaysThroughToday(timezone, n) {
  if (!timezone || n < 1) {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date(end);
    start.setDate(start.getDate() - (n - 1));
    return { start, end };
  }
  const todayYmd = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const todayAnchor = toDate(`${todayYmd}T12:00:00`, { timeZone: timezone });
  const startAnchor = subDays(todayAnchor, n - 1);
  const startYmd = formatInTimeZone(startAnchor, timezone, 'yyyy-MM-dd');
  return {
    start: storeStartOfCalendarDay(timezone, startYmd),
    end: storeEndOfCalendarDay(timezone, todayYmd),
  };
}

export function storeMatchRangeToday(timezone) {
  if (!timezone) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return { start: new Date(d), end: new Date(d) };
  }
  const ymd = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  return {
    start: storeStartOfCalendarDay(timezone, ymd),
    end: storeEndOfCalendarDay(timezone, ymd),
  };
}

export function storeMatchRangeYesterday(timezone) {
  if (!timezone) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 1);
    return { start: new Date(d), end: new Date(d) };
  }
  const todayYmd = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const yesterdayAnchor = subDays(toDate(`${todayYmd}T12:00:00`, { timeZone: timezone }), 1);
  const ymd = formatInTimeZone(yesterdayAnchor, timezone, 'yyyy-MM-dd');
  return {
    start: storeStartOfCalendarDay(timezone, ymd),
    end: storeEndOfCalendarDay(timezone, ymd),
  };
}

/** Current calendar week (Mon–Sun) in `timezone`, through today’s calendar day as end. */
export function storeMatchRangeThisWeek(timezone) {
  if (!timezone) {
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: WEEK_STARTS_ON });
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    return { start, end };
  }
  const todayYmd = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const weekMondayNoon = storeMondayNoonOfWeekContainingYmd(timezone, todayYmd);
  const startYmd = formatInTimeZone(weekMondayNoon, timezone, 'yyyy-MM-dd');
  return {
    start: storeStartOfCalendarDay(timezone, startYmd),
    end: storeEndOfCalendarDay(timezone, todayYmd),
  };
}

/** Previous full calendar week (Mon–Sun) in `timezone`. */
export function storeMatchRangeLastWeek(timezone) {
  if (!timezone) {
    const now = new Date();
    const thisWeekStart = startOfWeek(now, { weekStartsOn: WEEK_STARTS_ON });
    const lastWeekEnd = subDays(thisWeekStart, 1);
    const start = startOfWeek(lastWeekEnd, { weekStartsOn: WEEK_STARTS_ON });
    const end = endOfWeek(lastWeekEnd, { weekStartsOn: WEEK_STARTS_ON });
    return { start, end };
  }
  const todayYmd = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const thisWeekMondayNoon = storeMondayNoonOfWeekContainingYmd(timezone, todayYmd);
  const lastWeekMondayNoon = subDays(thisWeekMondayNoon, 7);
  const lastWeekSundayNoon = subDays(thisWeekMondayNoon, 1);
  const startYmd = formatInTimeZone(lastWeekMondayNoon, timezone, 'yyyy-MM-dd');
  const endYmd = formatInTimeZone(lastWeekSundayNoon, timezone, 'yyyy-MM-dd');
  return {
    start: storeStartOfCalendarDay(timezone, startYmd),
    end: storeEndOfCalendarDay(timezone, endYmd),
  };
}

export function storeMatchRangeThisMonth(timezone) {
  if (!timezone) {
    const time = new Date();
    time.setDate(1);
    const endTime = new Date();
    return { start: time, end: endTime };
  }
  const todayYmd = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const startYmd = `${todayYmd.slice(0, 7)}-01`;
  return {
    start: storeStartOfCalendarDay(timezone, startYmd),
    end: storeEndOfCalendarDay(timezone, todayYmd),
  };
}

export function storeMatchRangeLastMonth(timezone) {
  if (!timezone) {
    const time = new Date();
    time.setMonth(time.getMonth() - 1);
    time.setDate(1);
    const endTime = new Date();
    endTime.setDate(0);
    return { start: time, end: endTime };
  }
  const yyyymmdd = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const firstThisMonth = toDate(`${yyyymmdd.slice(0, 7)}-01T12:00:00`, { timeZone: timezone });
  const lastPrevMonth = subDays(firstThisMonth, 1);
  const endYmd = formatInTimeZone(lastPrevMonth, timezone, 'yyyy-MM-dd');
  const startYmd = `${endYmd.slice(0, 7)}-01`;
  return {
    start: storeStartOfCalendarDay(timezone, startYmd),
    end: storeEndOfCalendarDay(timezone, endYmd),
  };
}

export function storeMatchRangeThisYear(timezone) {
  if (!timezone) {
    const time = new Date();
    time.setDate(1);
    time.setMonth(0);
    const endTime = new Date();
    return { start: time, end: endTime };
  }
  const y = formatInTimeZone(new Date(), timezone, 'yyyy');
  const todayYmd = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const startYmd = `${y}-01-01`;
  return {
    start: storeStartOfCalendarDay(timezone, startYmd),
    end: storeEndOfCalendarDay(timezone, todayYmd),
  };
}

export function storeMatchRangeLastYear(timezone) {
  if (!timezone) {
    const time = new Date();
    time.setDate(1);
    time.setMonth(0);
    time.setFullYear(time.getFullYear() - 1);
    const endTime = new Date();
    endTime.setMonth(11);
    endTime.setDate(31);
    endTime.setFullYear(endTime.getFullYear() - 1);
    return { start: time, end: endTime };
  }
  const y = Number(formatInTimeZone(new Date(), timezone, 'yyyy')) - 1;
  const startYmd = `${y}-01-01`;
  const endYmd = `${y}-12-31`;
  return {
    start: storeStartOfCalendarDay(timezone, startYmd),
    end: storeEndOfCalendarDay(timezone, endYmd),
  };
}

/** Trigger label: calendar dates in the store timezone. */
export function formatMatchRangeForDisplay(timezone, active, match) {
  const s = match?.start;
  const e = match?.end;
  if (s == null || e == null) {
    if (typeof active === 'string' && active.length) return active;
    return 'Select range';
  }
  if (!timezone) {
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    const a = s instanceof Date ? s : new Date(s);
    const b = e instanceof Date ? e : new Date(e);
    return `${a.toLocaleDateString('en-US', opts)} - ${b.toLocaleDateString('en-US', opts)}`;
  }
  const left = formatInTimeZone(s, timezone, 'MMM d, yyyy');
  const right = formatInTimeZone(e, timezone, 'MMM d, yyyy');
  return `${left} - ${right}`;
}
